import { createPlanPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { isMeteredCreditProductOnly, isNotMeteredCreditProductOnly } from '@vy/services';
import type { Currency, FixedProductInput, UsageProductInput } from '@vy/types';
import { Counter } from '../../../../../../components/design/Counter';
import { ErrorIcon } from '../../../../../../components/icons/ErrorIcon';
import type { TabRecord } from '../../../../../../utils/types/tab-record';
import { FixedChargesTabContent } from '../create-plan-page-tabs/FixedChargesTabContent/FixedChargesTabContent';
import { PromotionChargesTabContent } from '../create-plan-page-tabs/PromotionChargesTabContent/PromotionChargesTabContent';
import { UsageChargesTabContent } from '../create-plan-page-tabs/UsageChargesTabContent/UsageChargesTabContent';
import type { CreatePlanTab } from '../create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../create-plan-page-types/create-plan-tab';

export type GetCreatePlanTabsPayload = {
  fixedProducts: FixedProductInput[];
  usageProducts: UsageProductInput[];
  promotionProducts: FixedProductInput[];
  currency: Currency;
  doesHaveError?: (tab: CreatePlanTab) => boolean;
  isParentAgreement?: boolean;
};

export function getCreatePlanTabs({
  fixedProducts,
  usageProducts,
  promotionProducts,
  currency,
  doesHaveError,
  isParentAgreement,
}: GetCreatePlanTabsPayload): TabRecord<CreatePlanTab> | Partial<TabRecord<CreatePlanTab>> {
  const meteredCredits = usageProducts.filter(isMeteredCreditProductOnly);
  const usageProductsWithoutMeteredCredits = usageProducts.filter(isNotMeteredCreditProductOnly);
  const creditsAndDiscounts = [...promotionProducts, ...meteredCredits];

  const serviceFees = {
    id: 0,
    render: (
      <FixedChargesTabContent
        fixedProducts={fixedProducts}
        currency={currency}
        isParentAgreement={isParentAgreement}
      />
    ),
    label: (
      <div className="flex items-center space-x-2">
        <span>Service Fees</span>
        <Counter
          className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
          count={fixedProducts.length}
        />
        {doesHaveError?.(CREATE_PLAN_TABS.serviceFees) && (
          <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]" />
        )}
      </div>
    ),
    selectorAttribute: getSelectorAttribute(createPlanPageSelectors.tabs.buttons.serviceFeesTab),
  };

  if (isParentAgreement) {
    return { [CREATE_PLAN_TABS.serviceFees]: serviceFees } as TabRecord<CreatePlanTab>;
  }

  return {
    [CREATE_PLAN_TABS.serviceFees]: serviceFees,
    [CREATE_PLAN_TABS.tierAndVolumePricing]: {
      id: 1,
      render: (
        <UsageChargesTabContent usageProducts={usageProductsWithoutMeteredCredits} currency={currency} />
      ),
      label: (
        <div className="flex items-center space-x-2">
          <span>Usage-based Fees</span>
          <Counter
            className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
            count={usageProductsWithoutMeteredCredits.length}
          />
          {doesHaveError?.(CREATE_PLAN_TABS.tierAndVolumePricing) && (
            <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]" />
          )}
        </div>
      ),
      selectorAttribute: getSelectorAttribute(createPlanPageSelectors.tabs.buttons.tierAndVolumePricingTab),
    },
    [CREATE_PLAN_TABS.promotions]: {
      id: 2,
      render: (
        <PromotionChargesTabContent
          promotions={promotionProducts}
          meteredCredits={meteredCredits}
          currency={currency}
        />
      ),
      label: (
        <div className="flex items-center space-x-2">
          <span>Credits & Discounts</span>
          <Counter
            className="group-[.is-selected]:bg-[#CCD7FC] group-[.is-selected]:text-[#458FFF]"
            count={creditsAndDiscounts.length}
          />
          {doesHaveError?.(CREATE_PLAN_TABS.promotions) && (
            <ErrorIcon className="fill-[#FC9592] group-[.is-selected]:fill-[#FF154D]" />
          )}
        </div>
      ),
      selectorAttribute: getSelectorAttribute(createPlanPageSelectors.tabs.buttons.promotionsTab),
    },
  };
}
